import {
  BulkCreateUpdateFinanceCostCenterMembershipDocument,
  EmployeeCostCentersDocument,
} from 'generated/resources/graphql'
import { noop } from 'lodash'
import moment from 'moment'

import { useCurrentSession } from 'modules/core/hooks/useCurrentSession'
import { useUnsafeMemo } from 'modules/core/hooks/useMemo'
import { useMutation } from 'modules/core/hooks/useMutation'
import { useQuery } from 'modules/core/hooks/useQuery'
import { handleResult } from 'modules/core/lib/handleMutation'

interface Props {
  employeeId: number
  legalEntityId: number
}

export interface DimensionValue {
  id: string
  name: string
}

interface BaseDimensionAssignment {
  id: number
  name: string
  options: DimensionValue[]
}

export type MutipleValueDimensionAssignment = BaseDimensionAssignment & {
  type: 'multiple-value'
  assignments: (DimensionValue & { percentage: number })[]
}

const COST_CENTER_DIMENSION_ID = -1
const useEmployeeCostCentersAssignments = ({ employeeId, legalEntityId }: Props) => {
  const { company } = useCurrentSession()

  const [bulkCreateUpdateFinanceCostCenterMembership] = useMutation(
    BulkCreateUpdateFinanceCostCenterMembershipDocument,
    {
      extractorFn: (newData) =>
        newData.financeMutations.bulkCreateUpdateFinanceCostCenterMembership,
    }
  )

  const { data, loading, refetch } = useQuery(EmployeeCostCentersDocument, {
    variables: {
      companyId: company.id,
      employeeId: employeeId,
      legalEntityId: legalEntityId,
    },
  })

  const saveCostCentersAssignments = async (
    employeeId: number,
    dimension: MutipleValueDimensionAssignment
  ) => {
    const variables = {
      employeeId: Number(employeeId),
      memberships: dimension.assignments.map((assignment) => ({
        percentage: assignment.percentage,
        costCenterId: Number(assignment.id),
        startDate: moment().toISOString(),
      })),
      companyId: company.id,
    }

    const response = await bulkCreateUpdateFinanceCostCenterMembership({
      variables,
    })

    const result = handleResult(response, noop)
    return result.success
  }

  const costCentersManageCostCenters =
    data?.finance._policies.costCentersManageCostCenters.can ?? false

  const costCenterMembershipsSeeCostCenterMemberships =
    data?.employees.employee?._policies?.seeCostCenterMembershipsFor.can ?? false

  const thereAreCostCenters = (data?.finance.costCentersConnection?.nodes?.length || 0) > 0

  const costCentersWithAssignments: MutipleValueDimensionAssignment[] = useUnsafeMemo(() => {
    if (loading) {
      return []
    }

    const costCenterMemberships =
      data?.employees.employee?.financecostcentermembershipsConnection.nodes || []

    const costCenters = data?.finance.costCentersConnection.nodes || []

    const res: MutipleValueDimensionAssignment[] = []

    if (costCenters.length > 0) {
      res.push({
        type: 'multiple-value',
        id: COST_CENTER_DIMENSION_ID,
        name: 'Cost Centers',
        options: costCenters.map((c) => ({
          id: c.id.toString(),
          name: c.name,
        })),
        assignments: costCenterMemberships.map((m) => ({
          id: m.costCenter.id.toString(),
          name: m.costCenter.name,
          percentage: m.percentage,
        })),
      })
    }

    return res
  }, [data, loading])

  return {
    isLoading: loading,
    costCentersManageCostCenters,
    costCentersWithAssignments,
    thereAreCostCenters,
    saveCostCentersAssignments,
    costCenterMembershipsSeeCostCenterMemberships,
    refetch,
  }
}

export default useEmployeeCostCentersAssignments
